/**
 * Staff Roles & Areas — the in-app editor for the ONE staff-role registry
 * (Phase 5 / S1). Roles + org areas here drive the Staff Positions form and
 * every canonical position slug. OL Mongo (`staff_role_registry`) is the SOT.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Plus, Trash2, Save, ShieldCheck, Building2, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StaffRolesPanel({ orgId }) {
  const [data, setData] = useState({ roles: [], areas: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRole, setNewRole] = useState({ label: '', scope: 'team', area: 'support' });
  const [newArea, setNewArea] = useState({ label: '' });

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ol-canonical/staff-registry/${orgId}`, { credentials: 'include' });
      const d = await r.json();
      setData(d?.data || { roles: [], areas: [] });
    } catch (e) {
      toast.error('Could not load the staff role registry');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const addItem = async (kind, body) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/ol-canonical/staff-registry/${orgId}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, ...body }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Failed');
      toast.success(`${kind === 'role' ? 'Role' : 'Area'} added`);
      if (kind === 'role') setNewRole({ label: '', scope: 'team', area: 'support' });
      else setNewArea({ label: '' });
      await load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const patchItem = async (kind, code, body) => {
    try {
      const r = await fetch(`${API}/api/ol-canonical/staff-registry/${orgId}/${kind}/${code}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Failed');
      toast.success('Saved');
      await load();
    } catch (e) {
      toast.error(String(e.message || e));
    }
  };

  const deleteItem = async (kind, code) => {
    try {
      const r = await fetch(`${API}/api/ol-canonical/staff-registry/${orgId}/${kind}/${code}`, {
        method: 'DELETE', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Failed');
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(String(e.message || e));
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 text-sm p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading staff roles…</div>;
  }

  const teamRoles = data.roles.filter(r => (r.scope || 'team') === 'team');
  const orgRoles = data.roles.filter(r => r.scope === 'org');

  const RoleRow = ({ r }) => (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-800/60" data-testid={`staff-role-row-${r.code}`}>
      <span className="text-[10px] font-mono text-slate-500 w-16 shrink-0">{r.code}</span>
      <Input
        defaultValue={r.label}
        className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-sm"
        onBlur={(e) => { if (e.target.value !== r.label) patchItem('role', r.code, { label: e.target.value }); }}
        data-testid={`staff-role-label-${r.code}`}
      />
      <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-900/40 h-8 px-2"
        onClick={() => deleteItem('role', r.code)} data-testid={`staff-role-delete-${r.code}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="staff-roles-panel">
      <p className="text-xs text-slate-400 max-w-2xl">
        These roles and areas power the <b>Staff Positions</b> form and every canonical position slug
        (e.g. <span className="font-mono">bk-vb-hc</span>, <span className="font-mono">org-board-pres</span>).
        Editing a label here updates it everywhere; the short code is fixed because it lives inside the slug.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Team roles */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-200 flex items-center gap-2"><Users className="h-4 w-4 text-sky-400" /> Team Roles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {teamRoles.map(r => <RoleRow key={r.code} r={r} />)}
          </CardContent>
        </Card>

        {/* Org roles */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-200 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-purple-400" /> Organization Roles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {orgRoles.map(r => <RoleRow key={r.code} r={r} />)}
          </CardContent>
        </Card>
      </div>

      {/* Add a role */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200 flex items-center gap-2"><Plus className="h-4 w-4 text-emerald-400" /> Add a role</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-slate-400">Label</Label>
            <Input value={newRole.label} placeholder="e.g. Video Coordinator"
              onChange={(e) => setNewRole({ ...newRole, label: e.target.value })}
              className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-sm" data-testid="new-role-label" />
          </div>
          <div className="w-40">
            <Label className="text-xs text-slate-400">Scope</Label>
            <Select value={newRole.scope} onValueChange={(v) => setNewRole({ ...newRole, scope: v })}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-8" data-testid="new-role-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="org">Organization</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {newRole.scope === 'org' && (
            <div className="w-40">
              <Label className="text-xs text-slate-400">Default area</Label>
              <Select value={newRole.area} onValueChange={(v) => setNewRole({ ...newRole, area: v })}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-8" data-testid="new-role-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {data.areas.map(a => <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button disabled={saving || !newRole.label.trim()} onClick={() => addItem('role', newRole)}
            className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="add-role-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Add role
          </Button>
        </CardContent>
      </Card>

      {/* Areas */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200 flex items-center gap-2"><Building2 className="h-4 w-4 text-amber-400" /> Organization Areas</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {data.areas.map(a => (
            <div key={a.code} className="flex items-center gap-2 py-1.5 border-b border-slate-800/60" data-testid={`staff-area-row-${a.code}`}>
              <span className="text-[10px] font-mono text-slate-500 w-16 shrink-0">{a.code}</span>
              <Input defaultValue={a.label} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-sm"
                onBlur={(e) => { if (e.target.value !== a.label) patchItem('area', a.code, { label: e.target.value }); }}
                data-testid={`staff-area-label-${a.code}`} />
              <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-900/40 h-8 px-2"
                onClick={() => deleteItem('area', a.code)} data-testid={`staff-area-delete-${a.code}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <div className="flex items-end gap-3 pt-3">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-slate-400">New area label</Label>
              <Input value={newArea.label} placeholder="e.g. Fundraising"
                onChange={(e) => setNewArea({ label: e.target.value })}
                className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-sm" data-testid="new-area-label" />
            </div>
            <Button disabled={saving || !newArea.label.trim()} onClick={() => addItem('area', newArea)}
              className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="add-area-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add area
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
